// File: frontend/src/components/common/PaneFilterBar.tsx
// src/components/common/PaneFilterBar.tsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { tr } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { InputClearButton } from "./InputClearButton";
import "./PaneFilterBar.css";
import { useTranslation } from "react-i18next";
import { SportTypeEnum } from "../../models/enums/SportTypeEnum";
import { getDateRangeForFilter, ALL_SPORT_TYPES } from "../../hooks/useMatches";
import { CountryCountModel, TopicChannelCountModel } from "../../models/CountryCountModel";
import { useAuth } from "../../context/AuthContext";
import { clubService } from "../../services/clubService";
import { teamService } from "../../services/teamService";
import { customTopicService } from "../../services/customTopicService";
import { fetchMatchCountries } from "../../services/matchService";
import { getPersonCountries } from "../../services/adminPersonService";
import { getPublicCountries, MdCountryDto } from "../../services/masterDataService";
import { TopicCategoryIconByKey } from "../../utils/topicCategoryIconMap";
// Compact count formatting ("2,4k") with the exact value kept for the tooltip
import { formatCompactWithTitle } from "../../utils/numberFormatUtils";
import {
  FaTimes,
  FaCheck,
  FaSearch,
  FaFutbol,
  FaBasketballBall,
  FaVolleyballBall,
  FaFootballBall,
  FaBaseballBall,
  FaHockeyPuck,
  FaGlobe,
  FaHashtag,
  FaPlus,
} from "react-icons/fa";
import { MdSportsHandball } from "react-icons/md";

// Register Turkish locale (still needed for react-datepicker itself, not i18next)
registerLocale("tr", tr);

type PaneMode = "matches" | "people" | "clubs" | "teams" | "topics" | "istikram" | "birak";

// Popover tabs. Each pane mode exposes a fixed set of tabs; single-filter
// domains show a one-tab strip so the layout stays identical across panes.
type FilterTab = "sport" | "date" | "country" | "channel";

// Mirrors the (non-exported) DateFilterType in useMatches — same convention the old MenuBar used
type DateFilterType = "all" | "lastMonth" | "lastWeek" | "yesterday" | "today" | "tomorrow" | "thisWeek" | "month" | "custom";

// Default date preset of useMatches — used to decide whether the date chip counts as an "active" filter
// Keep in sync with the defaultFilter in useMatches.ts
// Default date window — must stay in sync with useMatches.loadFilterFromStorage.
// Drives the active-filter badge count and the "reset date" chip visibility.
// "all" (Tüm Zamanlar) is the default: the match pane is an activity list, so a
// date window is opt-in. As a side effect the default state renders no date
// chip at all, which keeps the chip row short.
const DEFAULT_DATE_FILTER: DateFilterType = "all";

// Tab set per pane mode. Matches carries three filters; the other domains
// carry one, rendered as a single-tab strip for visual consistency.
const TABS_BY_MODE: Record<PaneMode, FilterTab[]> = {
  matches:  ["date", "sport", "country"],
  clubs:    ["country"],
  people:   ["country"],
  teams:    ["country"],
  topics:   ["channel"],
  // İstikram and Bırak are themselves special channels of "topics" (a topic's
  // FIRST channel routes it into one or the other instead of Gündem) — they
  // don't carry their own "kanal" sub-filter, so no tabs render at all and
  // the bar hides itself entirely (see the early return below).
  istikram: [],
  birak:    [],
};

interface PaneFilterBarProps {
  paneMode: PaneMode;
  // Matches — sport + date filter (moved here from the old full-width MenuBar)
  matchSportTypes: SportTypeEnum[];
  matchDateFilterType: DateFilterType;
  matchStartDate?: Date;
  matchEndDate?: Date;
  // Matches — country filter (either side's club country, null = all countries)
  matchCountry?: string | null;
  // Matches — "Canlı" toggle: true narrows the pane to matches in progress
  matchLiveOnly?: boolean;
  onMatchFilterChange: (newFilter: {
    sportTypes?: SportTypeEnum[];
    dateFilterType?: DateFilterType;
    startDate?: Date;
    endDate?: Date;
    country?: string | null;
    liveOnly?: boolean;
  }) => void;
  // Clubs — country filter
  clubsCountry: string | null;
  onClubsCountryChange: (code: string | null) => void;
  // People — country (nationality) filter
  peopleCountry: string | null;
  onPeopleCountryChange: (code: string | null) => void;
  // Teams — country filter (parent club country)
  teamsCountry: string | null;
  onTeamsCountryChange: (code: string | null) => void;
  // Custom topics — channel ("kanal") filter
  topicsChannelId: number | null;
  onTopicsChannelChange: (categoryId: number | null) => void;
  // Custom topics — opens the "Başlık Öner" suggestion modal (owned by LeftPane).
  // Rendered as a right-aligned button in the filter row, topics mode only.
  onSuggestTopic?: () => void;
}

/**
 * Count badge shown at the right edge of a filter list row.
 *
 * Large counts are shortened ("2.434" -> "2,4k") so a four or five digit number
 * never pushes the channel name out of the row; the exact value stays available
 * as a native tooltip on hover. Counts below 1000 are rendered in full and get
 * no tooltip, since there is nothing to reveal.
 */
const FilterCount: React.FC<{ value: number }> = ({ value }) => {
  const { text, title } = formatCompactWithTitle(value);
  return (
    <span className="pane-filter-bar__list-count" title={title}>
      {text}
    </span>
  );
};

/** Synthetic country code the backend uses for matches between clubs of
    different countries — rendered with a globe icon instead of a flag. */
const INTERNATIONAL_COUNTRY = "INTL";

/** Flag asset path — same lipis/flag-icons set served via Nginx as elsewhere in the app */
// Our own country codes for the UK's constituent nations are 3-letter pseudo-ISO
// codes (ENG/SCO/WAL/NIR — see createtables.sql), since there's no real ISO
// 3166-1 alpha-2 code for them. flag-icons doesn't have flags under those codes
// either; it uses its own "gb-xxx" subdivision codes instead. Without this map,
// slice(0, 2) either points at nothing ("ENG" -> "en", no such flag) or silently
// collides with an unrelated real country ("SCO" -> "sc" = Seychelles).
const UK_NATION_FLAG_CODES: Record<string, string> = {
  ENG: "gb-eng",
  SCO: "gb-sct",
  WAL: "gb-wls",
  NIR: "gb-nir",
};

const flagUrl = (code: string): string => {
  const upper = code.toUpperCase();
  const flagCode = UK_NATION_FLAG_CODES[upper] ?? code.slice(0, 2).toLowerCase();
  return `/media/flags/4x3/${flagCode}.svg`;
};

export const PaneFilterBar: React.FC<PaneFilterBarProps> = ({
  paneMode,
  matchSportTypes,
  matchDateFilterType,
  matchStartDate,
  matchEndDate,
  matchCountry = null,
  matchLiveOnly = false,
  onMatchFilterChange,
  clubsCountry,
  onClubsCountryChange,
  peopleCountry,
  onPeopleCountryChange,
  teamsCountry,
  onTeamsCountryChange,
  topicsChannelId,
  onTopicsChannelChange,
  onSuggestTopic,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isRookie = user?.role?.toUpperCase() === "ROOKIE";

  const [open, setOpen] = useState(false);
  // Currently selected popover tab — always the first tab of the pane mode on mount
  const [activeTab, setActiveTab] = useState<FilterTab>(TABS_BY_MODE[paneMode][0]);
  const [countrySearch, setCountrySearch] = useState("");
  // Channel ("kanal") list search — same UX as the country search; makes the
  // list usable once the channel count grows (e.g. 100+ channels)
  const [channelSearch, setChannelSearch] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(matchStartDate || null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(matchEndDate || null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Lazy-loaded filter options, cached per domain for the session
  const [matchCountries, setMatchCountries]   = useState<CountryCountModel[] | null>(null);
  const [clubCountries, setClubCountries]     = useState<CountryCountModel[] | null>(null);
  const [personCountries, setPersonCountries] = useState<CountryCountModel[] | null>(null);
  const [teamCountries, setTeamCountries]     = useState<CountryCountModel[] | null>(null);
  const [channels, setChannels]               = useState<TopicChannelCountModel[] | null>(null);
  // Master-data countries — provides display names for the raw country codes
  const [mdCountries, setMdCountries]         = useState<MdCountryDto[] | null>(null);
  const [optionsLoading, setOptionsLoading]   = useState(false);

  // Close the popover when the pane mode changes — filters are per-domain.
  // The active tab resets to the first tab of the new mode.
  useEffect(() => {
    setOpen(false);
    setShowDatePicker(false);
    setCountrySearch("");
    setChannelSearch("");
    setActiveTab(TABS_BY_MODE[paneMode][0]);
  }, [paneMode]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The calendar itself lives outside rootRef's DOM subtree because it's
      // rendered via portalId into document.body (see the DatePicker props
      // below). Without this check, clicking a day counts as an "outside"
      // click and closes the popover before the selection can register.
      const datePickerPortal = document.getElementById("pane-filter-datepicker-portal");
      if (datePickerPortal && datePickerPortal.contains(target)) return;
      if (rootRef.current && !rootRef.current.contains(target)) {
        setOpen(false);
        setShowDatePicker(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  /**
   * Lazy-load the option list needed by the given tab of the current pane the
   * first time it is shown. Results are cached in state so re-opening or
   * switching back to the tab costs no extra call. Sport/date tabs are static
   * and need no fetch.
   */
  const ensureOptionsLoaded = useCallback(async (tab: FilterTab) => {
    try {
      setOptionsLoading(true);
      // Country display names are shared by matches/clubs/people/teams — fetch once
      if (tab === "country" && mdCountries === null) {
        getPublicCountries().then(setMdCountries).catch(() => setMdCountries([]));
      }
      if (tab === "country" && paneMode === "matches" && matchCountries === null) {
        setMatchCountries(await fetchMatchCountries());
      } else if (tab === "country" && paneMode === "clubs" && clubCountries === null) {
        setClubCountries(await clubService.getClubCountries());
      } else if (tab === "country" && paneMode === "people" && personCountries === null) {
        setPersonCountries(await getPersonCountries());
      } else if (tab === "country" && paneMode === "teams" && teamCountries === null) {
        setTeamCountries(await teamService.getTeamCountries());
      } else if (tab === "channel" && paneMode === "topics" && channels === null) {
        setChannels(await customTopicService.getTopicChannels());
      }
    } catch (err) {
      console.error("[PaneFilterBar] options load error:", err);
    } finally {
      setOptionsLoading(false);
    }
  }, [paneMode, mdCountries, matchCountries, clubCountries, personCountries, teamCountries, channels]);

  // One options panel, opened per category button. Clicking the already-open
  // category closes the panel; clicking another switches the panel content.
  const handleCategoryClick = (tab: FilterTab) => {
    if (open && activeTab === tab) {
      setOpen(false);
      setShowDatePicker(false);
      return;
    }
    setActiveTab(tab);
    setOpen(true);
    setCountrySearch("");
    setChannelSearch("");
    setShowDatePicker(false);
    ensureOptionsLoaded(tab);
  };

  // ── Country helpers ────────────────────────────────────────────────────────

  const countryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (mdCountries ?? []).forEach((c) => map.set(c.code.toUpperCase(), c.displayName));
    return map;
  }, [mdCountries]);

  const countryLabel = (code: string) =>
    code === INTERNATIONAL_COUNTRY
      ? t("pane_filter_international", "Uluslararası")
      : countryNameMap.get(code.toUpperCase()) ?? code;

  const currentCountryOptions: CountryCountModel[] | null =
    paneMode === "matches" ? matchCountries :
    paneMode === "clubs"   ? clubCountries :
    paneMode === "people"  ? personCountries :
    paneMode === "teams"   ? teamCountries :
    null;

  const selectedCountry: string | null =
    paneMode === "matches" ? matchCountry :
    paneMode === "clubs"   ? clubsCountry :
    paneMode === "people"  ? peopleCountry :
    paneMode === "teams"   ? teamsCountry :
    null;

  const handleCountrySelect = (code: string | null) => {
    // Re-clicking the already-selected country DESELECTS it (back to all).
    // This is the only removal path on mobile, where the chip strip with its
    // × buttons is hidden.
    const next = code !== null && code === selectedCountry ? null : code;
    if (paneMode === "matches")     onMatchFilterChange({ country: next });
    else if (paneMode === "clubs")  onClubsCountryChange(next);
    else if (paneMode === "people") onPeopleCountryChange(next);
    else if (paneMode === "teams")  onTeamsCountryChange(next);
    setOpen(false);
  };

  const filteredCountryOptions = useMemo(() => {
    if (!currentCountryOptions) return [];
    const q = countrySearch.trim().toLowerCase();
    if (!q) return currentCountryOptions;
    return currentCountryOptions.filter((c) =>
      c.code.toLowerCase().includes(q) || countryLabel(c.code).toLowerCase().includes(q)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCountryOptions, countrySearch, countryNameMap]);

  // ── Channel helpers ────────────────────────────────────────────────────────

  // Search filter over the channel list — Turkish-aware lowercasing on both
  // sides so İ/I typed by the user still matches the lowercase channel names
  const filteredChannels = useMemo(() => {
    const list = channels ?? [];
    const q = channelSearch.trim().toLocaleLowerCase("tr");
    if (!q) return list;
    return list.filter((ch) => ch.name.toLocaleLowerCase("tr").includes(q));
  }, [channels, channelSearch]);

  const selectedChannel = useMemo(
    () => (channels ?? []).find((c) => c.id === topicsChannelId) ?? null,
    [channels, topicsChannelId]
  );

  const handleChannelSelect = (categoryId: number | null) => {
    // Re-clicking the already-selected channel DESELECTS it (back to all) —
    // same mobile-friendly removal path as the country list.
    const next = categoryId !== null && categoryId === topicsChannelId ? null : categoryId;
    onTopicsChannelChange(next);
    setOpen(false);
  };

  // Channel chip needs a label even before the options list is loaded — when a
  // filter is active but the list isn't fetched yet, load it in the background.
  // Country chips render flag-only but need the display name for the tooltip.
  useEffect(() => {
    if (paneMode === "topics" && topicsChannelId != null && channels === null) {
      customTopicService.getTopicChannels().then(setChannels).catch(() => setChannels([]));
    }
    if (selectedCountry && mdCountries === null) {
      getPublicCountries().then(setMdCountries).catch(() => setMdCountries([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paneMode, topicsChannelId, selectedCountry]);

  // ── Match (sport + date) helpers — logic carried over from the old MenuBar ──

  const isAllSports = matchSportTypes.length === 0;

  const handleAllSportsClick = () => {
    onMatchFilterChange({ sportTypes: [] });
    setShowDatePicker(false);
    // Picking an option closes the popover — one selection per open
    setOpen(false);
  };

  const handleSportTypeToggle = (sportType: SportTypeEnum) => {
    // Single-select: picking a sport replaces the whole selection with just
    // that sport; picking the already-active sport switches back to "All".
    // (Multi-select was dropped — with 7 sports the chip row exploded into
    // several wrapped lines and broke the compact filter bar layout.)
    // A legacy multi-sport array persisted in localStorage still renders as
    // multiple chips; any new click collapses it to a single selection.
    const isOnlySelected = matchSportTypes.length === 1 && matchSportTypes[0] === sportType;
    const newSportTypes: SportTypeEnum[] = isOnlySelected ? [] : [sportType];

    onMatchFilterChange({ sportTypes: newSportTypes });
    setShowDatePicker(false);
    // Picking an option closes the popover — one selection per open
    setOpen(false);
  };

  // Removes one sport from the chip row. With single-select this normally
  // clears back to "All"; for a legacy multi-sport selection it removes just
  // the clicked sport so leftover chips stay individually dismissible.
  const handleSportChipRemove = (sportType: SportTypeEnum) => {
    const remaining = matchSportTypes.filter((st) => st !== sportType);
    onMatchFilterChange({ sportTypes: remaining });
    setShowDatePicker(false);
  };

  const handleDateFilterChange = (filterType: DateFilterType) => {
    if (filterType === "custom") {
      // "Tarih Seç" only reveals the two date inputs — the popover stays open
      // until BOTH ends of the range are picked (see applyCustomRange).
      setShowDatePicker(!showDatePicker);
      if (matchDateFilterType !== "custom") {
        setCustomStartDate(null);
        setCustomEndDate(null);
      }
    } else {
      setShowDatePicker(false);
      // Re-clicking the active preset DESELECTS it back to the default
      // ("Tüm Zamanlar") — the mobile removal path, chips being hidden there.
      const next = filterType === matchDateFilterType ? DEFAULT_DATE_FILTER : filterType;
      onMatchFilterChange({
        dateFilterType: next,
        startDate: undefined,
        endDate: undefined,
      });
      // Picking a preset closes the popover — one selection per open
      setOpen(false);
    }
  };

  /**
   * Applies a custom range as soon as both ends are known and closes the whole
   * popover. Called from both date inputs' onChange, so the range is committed
   * the moment the second date is picked — the explicit apply (✓) button stays
   * as a fallback for keyboard users who typed the dates in.
   */
  const applyCustomRange = (start: Date | null, end: Date | null) => {
    if (!start || !end) return;
    onMatchFilterChange({
      dateFilterType: "custom",
      startDate: start,
      endDate: end,
    });
    setShowDatePicker(false);
    setOpen(false);
  };

  const handleCustomDateApply = () => {
    applyCustomRange(customStartDate, customEndDate);
  };

  // Memoize tooltips - recalculate when language changes
  const dateFilterTooltips = useMemo(() => {
    const getTooltip = (filterType: DateFilterType): string => {
      const { startDate, endDate } = getDateRangeForFilter(filterType);
      if (!startDate || !endDate) return "";

      const locale = i18n.language === "tr" ? "tr-TR" : "en-US";
      const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
      };

      const startStr = startDate.toLocaleDateString(locale, options);
      const endStr = endDate.toLocaleDateString(locale, options);

      if (startStr === endStr) {
        return startStr;
      }
      return `${startStr} - ${endStr}`;
    };

    return {
      all: t("menu_date_all"),
      lastMonth: getTooltip("lastMonth"),
      lastWeek: getTooltip("lastWeek"),
      yesterday: getTooltip("yesterday"),
      today: getTooltip("today"),
      tomorrow: getTooltip("tomorrow"),
      thisWeek: getTooltip("thisWeek"),
      month: getTooltip("month"),
    };
  }, [i18n.language, t]);

  // Map SportTypeEnum to translation keys
  const getSportTypeName = (sportType: SportTypeEnum | "ALL") => {
    switch (sportType) {
      case "ALL":
        return t("menu_sport_all");
      case SportTypeEnum.FOOTBALL:
        return t("menu_sport_football");
      case SportTypeEnum.BASKETBALL:
        return t("menu_sport_basketball");
      case SportTypeEnum.VOLLEYBALL:
        return t("menu_sport_volleyball");
      case SportTypeEnum.AMERICAN_FOOTBALL:
        return t("menu_sport_american_football");
      case SportTypeEnum.BASEBALL:
        return t("menu_sport_baseball");
      case SportTypeEnum.ICE_HOCKEY:
        return t("menu_sport_ice_hockey");
      case SportTypeEnum.HANDBALL:
        return t("menu_sport_handball");
      default:
        return "";
    }
  };

  // Get sport icon component
  const getSportIcon = (sportType: SportTypeEnum, size = 13) => {
    const iconProps = { size };
    switch (sportType) {
      case SportTypeEnum.FOOTBALL:
        return <FaFutbol {...iconProps} />;
      case SportTypeEnum.BASKETBALL:
        return <FaBasketballBall {...iconProps} />;
      case SportTypeEnum.VOLLEYBALL:
        return <FaVolleyballBall {...iconProps} />;
      case SportTypeEnum.AMERICAN_FOOTBALL:
        return <FaFootballBall {...iconProps} />;
      case SportTypeEnum.BASEBALL:
        return <FaBaseballBall {...iconProps} />;
      case SportTypeEnum.ICE_HOCKEY:
        return <FaHockeyPuck {...iconProps} />;
      case SportTypeEnum.HANDBALL:
        return <MdSportsHandball {...iconProps} />;
      default:
        return <FaFutbol {...iconProps} />;
    }
  };

  const getDateFilterLabel = (filterType: DateFilterType) => {
    switch (filterType) {
      case "all":       return t("menu_date_all");
      case "lastMonth": return t("menu_date_last_month");
      case "lastWeek":  return t("menu_date_last_week");
      case "yesterday": return t("menu_date_yesterday");
      case "today":     return t("menu_date_today");
      case "tomorrow":  return t("menu_date_tomorrow");
      case "thisWeek":  return t("menu_date_this_week");
      case "month":     return t("menu_date_this_month");
      case "custom":    return t("menu_date_select");
      default:          return "";
    }
  };

  // Tab labels — country/channel reuse the old section-label keys
  const getTabLabel = (tab: FilterTab) => {
    switch (tab) {
      case "sport":   return t("pane_filter_sport_label", "Spor");
      case "date":    return t("pane_filter_date_label", "Tarih");
      case "country": return t("pane_filter_country_label", "Ülke");
      case "channel": return t("pane_filter_channel_label", "Kanal");
      default:        return "";
    }
  };

  // ── Active filter markers ───────────────────────────────────────────────────
  // The old single "Filtrele" toggle carried an aggregate badge count; with one
  // button per category each button now marks its OWN active state instead.

  const isTabActive = (tab: FilterTab): boolean => {
    switch (tab) {
      case "sport":   return matchSportTypes.length > 0; // empty means "all sports"
      case "date":    return matchDateFilterType !== DEFAULT_DATE_FILTER;
      case "country": return !!selectedCountry;
      case "channel": return topicsChannelId != null;
      default:        return false;
    }
  };

  // Clears one category's filter directly from the × on its button — the
  // explicit, always-visible deselect path (crucial on mobile, where the chip
  // strip is hidden). Does not open or close the options panel.
  const clearTab = (tab: FilterTab) => {
    switch (tab) {
      case "sport":
        onMatchFilterChange({ sportTypes: [] });
        break;
      case "date":
        onMatchFilterChange({ dateFilterType: DEFAULT_DATE_FILTER, startDate: undefined, endDate: undefined });
        setShowDatePicker(false);
        break;
      case "country":
        if (paneMode === "matches")     onMatchFilterChange({ country: null });
        else if (paneMode === "clubs")  onClubsCountryChange(null);
        else if (paneMode === "people") onPeopleCountryChange(null);
        else if (paneMode === "teams")  onTeamsCountryChange(null);
        break;
      case "channel":
        onTopicsChannelChange(null);
        break;
    }
  };

  const dateFilterTypes: DateFilterType[] = [
    "all", "lastMonth", "lastWeek", "yesterday", "today", "tomorrow", "thisWeek", "month",
  ];

  const tabs = TABS_BY_MODE[paneMode];

  // İstikram/Bırak have no filter of their own — render nothing instead of an
  // empty shell. Must come after the hooks above (rules-of-hooks), before the
  // JSX below.
  if (tabs.length === 0) return null;

  // Hide the broken flag image and reveal the country-code fallback next to it,
  // so flag-only chips never collapse into a bare × when the SVG is missing.
  const onChipFlagError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;
    img.style.display = "none";
    const fallback = img.nextElementSibling as HTMLElement | null;
    if (fallback) fallback.style.display = "inline";
  };

  return (
    <div className="pane-filter-bar" ref={rootRef}>
      <div className="pane-filter-bar__row">
        {/* Category buttons — one per filter of the current domain: Tarih /
            Spor / Ülke for matches, Ülke for clubs/people/teams, Kanal for
            topics. Sits right next to the main search, so every filter is
            visible at a glance instead of hiding behind a generic "Filtrele"
            button. The options panel opens directly BELOW these buttons; a
            dot on a button marks that its filter is active. */}
        <div className="pane-filter-bar__cats">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`pane-filter-bar__cat-btn ${open && activeTab === tab ? "open" : ""} ${isTabActive(tab) ? "has-active" : ""}`}
              onClick={() => handleCategoryClick(tab)}
              title={getTabLabel(tab)}
            >
              <span>{getTabLabel(tab)}</span>
              {/* Active filter: an explicit, always-visible × clears THIS
                  category with one tap, without opening the panel. This is
                  the deselect path on mobile (no chips there). A span, not a
                  nested <button> — buttons cannot contain buttons. */}
              {isTabActive(tab) && (
                <span
                  role="button"
                  aria-label={t("pane_filter_remove", "Kaldır")}
                  className="pane-filter-bar__cat-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearTab(tab);
                  }}
                >
                  <FaTimes size={11} />
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Matches only: "Canlı" toggle, sitting immediately right of the
            category buttons. Deliberately styled unlike them — live red with a
            pulsing dot — because it is a state of the world, not a preference:
            it should read as "something is happening right now", and the pane
            has to advertise that at a glance. One tap narrows the list to
            matches in progress, another tap restores the full list. */}
        {paneMode === "matches" && (
          <button
            className={`pane-filter-bar__cat-btn pane-filter-bar__live${matchLiveOnly ? " active" : ""}`}
            onClick={() => onMatchFilterChange({ liveOnly: !matchLiveOnly })}
            title={t("pane_filter_live", "Canlı")}
            aria-label={t("pane_filter_live", "Canlı")}
            aria-pressed={matchLiveOnly}
            type="button"
          >
            <span>{t("pane_filter_live", "Canlı")}</span>
          </button>
        )}

        {/* Active filter chips — removable with ×. Sport and country chips are
            compact (icon/flag only, full name in the tooltip) so that all three
            match filters fit on a single row even in the narrow left pane. */}
        <div className="pane-filter-bar__chips">
          {/* Matches: one compact icon-only chip per selected sport */}
          {paneMode === "matches" && !isAllSports && matchSportTypes.map((sport) => (
            <button
              key={sport}
              className="pane-filter-bar__chip pane-filter-bar__chip--compact"
              onClick={() => handleSportChipRemove(sport)}
              title={`${getSportTypeName(sport)} — ${t("pane_filter_remove", "Kaldır")}`}
            >
              {getSportIcon(sport, 11)}
              <FaTimes size={13} className="pane-filter-bar__chip-x" />
            </button>
          ))}

          {/* Matches: date chip only when it differs from the default preset */}
          {paneMode === "matches" && matchDateFilterType !== DEFAULT_DATE_FILTER && (
            <button
              className="pane-filter-bar__chip"
              onClick={() => onMatchFilterChange({ dateFilterType: DEFAULT_DATE_FILTER, startDate: undefined, endDate: undefined })}
              title={t("pane_filter_remove", "Kaldır")}
            >
              <span>{getDateFilterLabel(matchDateFilterType)}</span>
              <FaTimes size={13} className="pane-filter-bar__chip-x" />
            </button>
          )}

          {/* Matches / Clubs / People / Teams: compact flag-only country chip.
              The country name lives in the tooltip; if the flag SVG is missing
              the country code is shown as a text fallback instead. */}
          {selectedCountry && (
            <button
              className="pane-filter-bar__chip pane-filter-bar__chip--compact"
              onClick={() => handleCountrySelect(null)}
              title={`${countryLabel(selectedCountry)} — ${t("pane_filter_remove", "Kaldır")}`}
            >
              {selectedCountry === INTERNATIONAL_COUNTRY ? (
                <FaGlobe size={11} />
              ) : (
                <>
                  <img
                    src={flagUrl(selectedCountry)}
                    alt=""
                    className="pane-filter-bar__chip-flag"
                    onError={onChipFlagError}
                  />
                  <span className="pane-filter-bar__chip-flag-fallback">{selectedCountry}</span>
                </>
              )}
              <FaTimes size={13} className="pane-filter-bar__chip-x" />
            </button>
          )}

          {/* Topics: channel chip */}
          {paneMode === "topics" && topicsChannelId != null && (
            <button
              className="pane-filter-bar__chip"
              onClick={() => handleChannelSelect(null)}
              title={t("pane_filter_remove", "Kaldır")}
            >
              {selectedChannel?.iconKey
                ? <TopicCategoryIconByKey iconKey={selectedChannel.iconKey} size={10} />
                : <FaHashtag size={10} />}
              <span>{selectedChannel?.name ?? `#${topicsChannelId}`}</span>
              <FaTimes size={13} className="pane-filter-bar__chip-x" />
            </button>
          )}
        </div>

        {/* Topics only: "Başlık Öner" — right-aligned, opens the suggestion modal.
            Hidden for rookies (çaylaklar) — they're not allowed to suggest topics. */}
        {paneMode === "topics" && onSuggestTopic && !isRookie && (
          <button
            className="pane-filter-bar__suggest"
            onClick={onSuggestTopic}
            title={t("suggest_topic_button", "Başlık Öner")}
            aria-label={t("suggest_topic_button", "Başlık Öner")}
          >
            <FaPlus size={9} />
            <span>{t("suggest_topic_button", "Başlık Öner")}</span>
          </button>
        )}
      </div>

      {/* ── Options panel — content of the clicked category button ── */}
      {open && (
        <div className="pane-filter-bar__popover">

          {/* The old in-popover tab strip is gone: the always-visible category
              buttons above ARE the tabs now. The panel renders only the active
              category's options. */}

          {/* SPORT — matches only, carried over from the old MenuBar */}
          {paneMode === "matches" && activeTab === "sport" && (
            <div className="pane-filter-bar__options-grid">
              <button
                className={`pane-filter-bar__option-btn ${isAllSports ? "active" : ""}`}
                onClick={handleAllSportsClick}
                title={getSportTypeName("ALL")}
              >
                <FaGlobe size={13} />
                <span>{t("menu_all")}</span>
              </button>
              {ALL_SPORT_TYPES.map((sport) => (
                <button
                  key={sport}
                  className={`pane-filter-bar__option-btn ${!isAllSports && matchSportTypes.includes(sport) ? "active" : ""}`}
                  onClick={() => handleSportTypeToggle(sport)}
                  title={getSportTypeName(sport)}
                >
                  {getSportIcon(sport)}
                  <span>{getSportTypeName(sport)}</span>
                </button>
              ))}
            </div>
          )}

          {/* DATE — matches only, carried over from the old MenuBar */}
          {paneMode === "matches" && activeTab === "date" && (
            <>
              <div className="pane-filter-bar__options-grid">
                {dateFilterTypes.map((ft) => (
                  <button
                    key={ft}
                    className={`pane-filter-bar__option-btn ${matchDateFilterType === ft ? "active" : ""}`}
                    onClick={() => handleDateFilterChange(ft)}
                    title={dateFilterTooltips[ft as keyof typeof dateFilterTooltips]}
                  >
                    <span>{getDateFilterLabel(ft)}</span>
                  </button>
                ))}
                <button
                  className={`pane-filter-bar__option-btn ${matchDateFilterType === "custom" ? "active" : ""}`}
                  onClick={() => handleDateFilterChange("custom")}
                >
                  <span>{t("menu_date_select")}</span>
                </button>
              </div>

              {showDatePicker && (
                <div className="pane-filter-bar__date-picker">
                  <DatePicker
                    selected={customStartDate}
                    onChange={(date: Date | null) => {
                      setCustomStartDate(date);
                      // Auto-apply when this completes an already half-picked range
                      if (date && customEndDate && date <= customEndDate) {
                        applyCustomRange(date, customEndDate);
                      }
                    }}
                    selectsStart
                    startDate={customStartDate}
                    endDate={customEndDate}
                    placeholderText={t("menu_date_start")}
                    className="date-picker-input"
                    popperPlacement="bottom-start"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    locale="tr"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    portalId="pane-filter-datepicker-portal"
                  />
                  <span className="pane-filter-bar__date-separator">-</span>
                  <DatePicker
                    selected={customEndDate}
                    onChange={(date: Date | null) => {
                      setCustomEndDate(date);
                      // Second date of the range picked → apply and close
                      if (date && customStartDate) {
                        applyCustomRange(customStartDate, date);
                      }
                    }}
                    selectsEnd
                    startDate={customStartDate}
                    endDate={customEndDate}
                    minDate={customStartDate || undefined}
                    placeholderText={t("menu_date_end")}
                    className="date-picker-input"
                    popperPlacement="bottom-end"
                    dateFormat="dd/MM/yyyy"
                    isClearable
                    locale="tr"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    portalId="pane-filter-datepicker-portal"
                  />
                  <button
                    onClick={handleCustomDateApply}
                    className="pane-filter-bar__date-apply"
                    disabled={!customStartDate || !customEndDate}
                    title={t("menu_date_apply")}
                  >
                    <FaCheck size={11} />
                  </button>
                </div>
              )}
            </>
          )}

          {/* COUNTRY — matches / clubs / people / teams share the same
              searchable country list with flags + counts */}
          {activeTab === "country" && (
            <>
              <div className="pane-filter-bar__search">
                <FaSearch size={10} />
                <input
                  type="text"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder={t("pane_filter_country_search", "ülke ara...")}
                  autoFocus
                />
                {countrySearch !== "" && (
                  <InputClearButton variant="inline" onClear={() => setCountrySearch("")} />
                )}
              </div>
              <div className="pane-filter-bar__list">
                <button
                  className={`pane-filter-bar__list-item ${!selectedCountry ? "active" : ""}`}
                  onClick={() => handleCountrySelect(null)}
                >
                  <FaGlobe size={13} className="pane-filter-bar__list-icon" />
                  <span className="pane-filter-bar__list-name">{t("pane_filter_all", "Tümü")}</span>
                </button>
                {optionsLoading && currentCountryOptions === null && (
                  <div className="pane-filter-bar__list-loading">{t("load_more_loading", "yükleniyor...")}</div>
                )}
                {filteredCountryOptions.map((c) => (
                  <button
                    key={c.code}
                    className={`pane-filter-bar__list-item ${selectedCountry === c.code ? "active" : ""}`}
                    onClick={() => handleCountrySelect(c.code)}
                  >
                    {c.code === INTERNATIONAL_COUNTRY ? (
                      <FaGlobe size={13} className="pane-filter-bar__list-icon" />
                    ) : (
                      <img
                        src={flagUrl(c.code)}
                        alt=""
                        className="pane-filter-bar__list-flag"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    )}
                    <span className="pane-filter-bar__list-name">{countryLabel(c.code)}</span>
                    <FilterCount value={c.count} />
                  </button>
                ))}
                {!optionsLoading && currentCountryOptions !== null && filteredCountryOptions.length === 0 && (
                  <div className="pane-filter-bar__list-empty">{t("pane_filter_no_results", "Sonuç yok")}</div>
                )}
              </div>
            </>
          )}

          {/* CHANNEL — topics: searchable channel ("kanal") list with icons +
              counts. The search box mirrors the country search — with 100+
              channels scrolling alone would be painful. "Tümü" stays pinned
              above the results regardless of the search term. */}
          {paneMode === "topics" && activeTab === "channel" && (
            <>
              <div className="pane-filter-bar__search">
                <FaSearch size={10} />
                <input
                  type="text"
                  value={channelSearch}
                  onChange={(e) => setChannelSearch(e.target.value)}
                  placeholder={t("pane_filter_channel_search", "kanal ara...")}
                  autoFocus
                />
                {channelSearch !== "" && (
                  <InputClearButton variant="inline" onClear={() => setChannelSearch("")} />
                )}
              </div>
              <div className="pane-filter-bar__list">
                <button
                  className={`pane-filter-bar__list-item ${topicsChannelId == null ? "active" : ""}`}
                  onClick={() => handleChannelSelect(null)}
                >
                  <FaGlobe size={13} className="pane-filter-bar__list-icon" />
                  <span className="pane-filter-bar__list-name">{t("pane_filter_all", "Tümü")}</span>
                </button>
                {optionsLoading && channels === null && (
                  <div className="pane-filter-bar__list-loading">{t("load_more_loading", "yükleniyor...")}</div>
                )}
                {filteredChannels.map((ch) => (
                  <button
                    key={ch.id}
                    className={`pane-filter-bar__list-item ${topicsChannelId === ch.id ? "active" : ""}`}
                    onClick={() => handleChannelSelect(ch.id)}
                  >
                    <span className="pane-filter-bar__list-icon">
                      {ch.iconKey
                        ? <TopicCategoryIconByKey iconKey={ch.iconKey} size={13} />
                        : <FaHashtag size={13} />}
                    </span>
                    <span className="pane-filter-bar__list-name">{ch.name}</span>
                    <FilterCount value={ch.count} />
                  </button>
                ))}
                {!optionsLoading && channels !== null && filteredChannels.length === 0 && (
                  <div className="pane-filter-bar__list-empty">{t("pane_filter_no_results", "Sonuç yok")}</div>
                )}
              </div>
            </>
          )}

        </div>
      )}
    </div>
  );
};